import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { durations, eases } from '@/shared/constants/motion.constants';
import { PAGE_TRANSITION_VARIANTS } from '@/shared/ui/animations';
import { useSearchParams } from 'react-router';
import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import { Button } from '@monksflow/monks-ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useQuery } from '@tanstack/react-query';
import { Check, Trash2Icon, Workflow, X } from 'lucide-react';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { projectFolderFilesQuery } from '@/entities/project-folder';
import { useHasProjectPermission } from '@/entities/user';
import {
  BulkAssignApprovalChainDialog,
  BulkRemoveApprovalChainDialog,
  type SelectedAssetApprovalChain,
} from '@/features/assign-asset-approval-chain';
import {
  BulkUnapproveDialog,
  summarizeBulkResults,
  useBulkApprove,
} from '@/features/bulk-approve-assets';
import { notificationService } from '@/lib/services/notification.service';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks/useDebounce/useDebounce';
import { FilterChipPopover, type FilterChipOption } from '@/shared/ui/FilterChipPopover';
import { SearchInput } from '@/shared/ui/SearchInput';
import { SortChipPopover, type SortChipOption } from '@/shared/ui/SortChipPopover';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { ProjectFileUploadGuard } from '@/features/project-file-upload-guard';
import { useFileUploadToFolder } from '../../hooks/useFileUploadToFolder';
import { AssetGrid } from '../AssetGrid';
import { useApprovalOverride } from '@/features/approve-asset-step';
import { useApprovalChainsEnabled } from '@/shared/hooks/useApprovalChainsEnabled';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { ScopedPermission } from '@/shared/ui';
import { useTranslation } from 'react-i18next';

type StatusTab = 'all' | 'to_review' | 'approved';
/** What the Status chip can be set to; "all" is its cleared state, not an option. */
type AssetStatusFilter = Exclude<StatusTab, 'all'>;
type SortOrder = 'DESC' | 'ASC';

/* eslint-disable i18next/no-literal-string -- URL query parameter names */
const STATUS_QUERY_PARAM = 'status';
const SORT_QUERY_PARAM = 'sort';
/* eslint-enable i18next/no-literal-string */

// `DESC` (newest first) is the server-side default, so it lives as the
// chip's reset row rather than a selectable option — selecting it just
// clears the sort.
const SORT_OLDEST_FIRST_ID = 'oldest-first';

function parseStatusTab(raw: string | null): StatusTab {
  return raw === 'to_review' || raw === 'approved' ? raw : 'all';
}

function parseSortOrder(raw: string | null): SortOrder {
  return raw === 'asc' ? 'ASC' : 'DESC';
}

interface Props {
  projectId: string;
  folderId: string;
  /**
   * The pane header owns the upload action, so it needs the picker this
   * section wires to the folder. Bound here rather than duplicating the upload
   * pipeline in the header.
   */
  uploadInputRef?: RefObject<HTMLInputElement | null>;
}

export function AssetsSection(props: Props) {
  const { t } = useTranslation('projects');
  const shouldReduceMotion = useReducedMotion();

  const assetSortChoices = useMemo<ReadonlyArray<SortChipOption>>(
    () => [
      {
        id: SORT_OLDEST_FIRST_ID,
        label: t('projectDetail.assetsSection.oldestFirst'),
        direction: 'asc',
      },
    ],
    [t],
  );

  const assetStatusChoices = useMemo<ReadonlyArray<FilterChipOption<AssetStatusFilter>>>(
    () => [
      {
        value: 'approved',
        label: t('projectDetail.assetsSection.approved'),
      },
      {
        value: 'to_review',
        label: t('projectDetail.assetsSection.inReview'),
      },
    ],
    [t],
  );

  const { projectId, folderId } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const statusTab = parseStatusTab(searchParams.get(STATUS_QUERY_PARAM));
  const sortOrder = parseSortOrder(searchParams.get(SORT_QUERY_PARAM));

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  const status: ProjectFileResponseDto['status'] | undefined =
    statusTab === 'all' ? undefined : statusTab;
  const name = debouncedSearch.trim() || undefined;

  const { data: filesData, isLoading: isFilesLoading } = useQuery(
    projectFolderFilesQuery(projectId, folderId, {
      status,
      name,
      sortBy: 'latest_version_created_at',
      sortOrder,
    }),
  );
  const files = useMemo(() => filesData?.items ?? [], [filesData]);

  // Bulk approval chain assignment is behind the `workflow_management`
  // flag (default off) as a kill-switch: with it off the cards carry no
  // checkbox, so there is nothing to select and no bar to raise.
  const approvalChainsEnabled = useApprovalChainsEnabled();
  const isManager = useApprovalOverride();
  // The bulk bar's actions all need it, so without it a selection could not be
  // acted on and the checkbox would be an invitation to nothing.
  const canEditDetails = useHasProjectPermission([PROJECT_PERMISSIONS.ProjectEditDetails]);
  const bulkRemoveApprovalChainEnabled = useBooleanFlagValue('approval_chain_bulk_remove', false);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const selectedCount = selectedIds.size;
  // Not a mode you turn on: picking the first asset is what starts it, and
  // clearing the last is what ends it. While it lasts the card itself toggles,
  // so a run of selections does not mean hitting the checkbox every time.
  const selectionMode = selectedCount > 0;
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isUnapproveDialogOpen, setIsUnapproveDialogOpen] = useState(false);
  const [isBulkRemoveDialogOpen, setIsBulkRemoveDialogOpen] = useState(false);

  const bulkApprove = useBulkApprove({ projectId, folderId });

  const toggleSelect = useCallback((file: ProjectFileResponseDto) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(file.id)) {
        next.delete(file.id);
      } else {
        next.add(file.id);
      }
      return next;
    });
  }, []);

  const exitSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(files.map((file) => file.id)));
  }, [files]);

  // Escape leaves selection the way it leaves any other transient mode. A
  // dialog raised from the bulk bar owns the key while it is open, so the two
  // do not both answer one press.
  useEffect(() => {
    if (!selectionMode || isBulkDialogOpen || isUnapproveDialogOpen) return;

    const leaveSelectionOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') exitSelection();
    };

    document.addEventListener('keydown', leaveSelectionOnEscape);
    return () => document.removeEventListener('keydown', leaveSelectionOnEscape);
  }, [selectionMode, isBulkDialogOpen, isUnapproveDialogOpen, exitSelection]);

  const selectedAssetIds = useMemo(() => Array.from(selectedIds), [selectedIds]);

  // Coarse per-action eligibility derived from the files-list approval chain status.
  // The DTO carries approval chain status/step counts but NOT per-reviewer assignee
  // identities, so this can't reflect full per-user eligibility — the BE still
  // skips assets the caller can't act on, and that shows in the result toast.
  // The goal is only to disable an action when *no* selected asset could
  // possibly be actionable.
  //   - Approve advances or starts an approval chain → not_started / in_progress.
  //   - Unapprove rewinds an approved step → completed, or in_progress with at
  //     least one approval already recorded (past step 1, or approvals on the
  //     current step).
  const selectedFiles = useMemo(
    () => files.filter((file) => selectedIds.has(file.id)),
    [files, selectedIds],
  );
  const isEveryLoadedAssetSelected = files.length > 0 && selectedFiles.length === files.length;
  // The list holds only the first page, so a folder can have more assets than were loaded,
  // and "All N" would then claim assets the selection does not cover.
  const isEveryFolderAssetSelected =
    isEveryLoadedAssetSelected && filesData?.total === files.length;
  const canApproveSelection = useMemo(
    () =>
      selectedFiles.some((file) => {
        const status = file.workflowProgress?.status;
        return status === 'in_progress' || status === 'not_started';
      }),
    [selectedFiles],
  );
  const canUnapproveSelection = useMemo(
    () =>
      selectedFiles.some((file) => {
        const progress = file.workflowProgress;
        if (!progress) return false;
        if (progress.status === 'completed') return true;
        return (
          progress.status === 'in_progress' &&
          (progress.currentStepPosition > 1 || progress.currentStepApprovedCount > 0)
        );
      }),
    [selectedFiles],
  );

  // The unassign endpoint is per chain, so the dialog needs the chains present in the
  // selection rather than the project's whole list.
  const chainsInSelection = useMemo<SelectedAssetApprovalChain[]>(() => {
    const byChainId = new Map<string, SelectedAssetApprovalChain>();
    for (const file of selectedFiles) {
      const progress = file.workflowProgress;
      if (!progress) continue;
      const existing = byChainId.get(progress.workflowDefinitionId);
      if (existing) {
        existing.assetIds.push(file.id);
      } else {
        byChainId.set(progress.workflowDefinitionId, {
          id: progress.workflowDefinitionId,
          name: progress.workflowName,
          assetIds: [file.id],
        });
      }
    }
    return Array.from(byChainId.values());
  }, [selectedFiles]);

  // Bulk approve fires immediately (no confirm) — only unapprove, which resets
  // steps, opens a warning dialog first. Both surface a per-asset result toast.
  const handleBulkApprove = useCallback(() => {
    if (selectedAssetIds.length === 0 || bulkApprove.isPending) return;
    bulkApprove.mutate(selectedAssetIds, {
      onSuccess: (result) => {
        // BE returns 200 with per-asset failures in the body; a fully-skipped
        // run resolves here too. Only show success if something actually changed.
        const summary = summarizeBulkResults(result.results, 'approved');
        if (result.results.some((r) => r.success)) {
          notificationService.showSuccess(summary);
        } else {
          notificationService.showWarning(summary);
        }
        exitSelection();
      },
      onError: (error) => {
        const { detail } = parseProblemDetail(
          error,
          t('projectDetail.assetsSection.failedToApproveAssets'),
        );
        notificationService.showError(detail);
      },
    });
  }, [selectedAssetIds, bulkApprove, exitSelection, t]);

  const ownInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = props.uploadInputRef ?? ownInputRef;
  const { startUpload, isError, resetError } = useFileUploadToFolder(projectId, folderId);

  // A selection only means anything against the assets it was made from, and
  // the bulk actions send ids rather than what is on screen. Narrowing the
  // result set would otherwise leave the bar raised over a grid that no longer
  // holds any of them, still able to approve or unapprove what the filter hid.
  const updateStatusTab = (next: StatusTab) => {
    exitSelection();
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'all') {
          params.delete(STATUS_QUERY_PARAM);
        } else {
          params.set(STATUS_QUERY_PARAM, next);
        }
        return params;
      },
      { replace: true },
    );
  };

  const sortValue = sortOrder === 'ASC' ? SORT_OLDEST_FIRST_ID : null;

  // Sort is excluded on purpose: reordering nothing is still nothing, so it is
  // never the reason a folder looks empty and clearing it would not help.
  const hasActiveFilters = statusTab !== 'all' || debouncedSearch.trim().length > 0;

  const clearFilters = () => {
    setSearchInput('');
    updateStatusTab('all');
  };

  // A folder with assets the filters exclude is not an empty folder — that one
  // is answered upstream, before this section renders at all.
  const hasNoMatches = !isFilesLoading && files.length === 0 && hasActiveFilters;

  const updateSortValue = (next: string | null) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === SORT_OLDEST_FIRST_ID) {
          params.set(SORT_QUERY_PARAM, 'asc');
        } else {
          // null → reset to the server default (DESC); keep the URL clean.
          params.delete(SORT_QUERY_PARAM);
        }
        return params;
      },
      { replace: true },
    );
  };

  return (
    <ProjectFileUploadGuard projectId={projectId} folderId={folderId} onResolved={startUpload}>
      {({ submit }) => (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) {
                submit(Array.from(e.target.files));
                e.target.value = '';
              }
            }}
          />

          <div className="relative">
            {/* Hidden, not unmounted: the bulk bar sits over this row, so the
                row still has to hold the space it occupies. `visibility` is what
                takes it out of the tab order, and transitioning it is what makes
                it wait for the fade instead of cutting it short.

                The opacity runs on the bar's own duration and curve so the two
                cross at the midpoint rather than one finishing early. That the
                curve is symmetric about (0.5, 0.5) is the whole reason the
                crossing lands in the centre.

                The two travel as a pair: both drop as selection begins and
                both rise as it ends. The toolbar borrows the page entrance's
                own `slideUp` states rather than restating its offset, so the
                row leaves and returns on exactly the motion everything else on
                these pages arrives on.

*/}
            <motion.div
              initial={false}
              animate={selectionMode ? 'away' : 'here'}
              variants={{
                away: PAGE_TRANSITION_VARIANTS.slideUp.initial,
                here: PAGE_TRANSITION_VARIANTS.slideUp.animate,
              }}
              transition={{
                duration: shouldReduceMotion ? 0 : durations.base,
                ease: eases.quadInOut,
              }}
              className={`flex flex-row items-center gap-xs transition-[visibility] duration-base ${selectionMode ? 'invisible' : ''}`}
              data-testid="folder-assets-toolbar"
            >
              <SearchInput
                size="lg"
                value={searchInput}
                // Clears on the raw input rather than the debounced term, so
                // the bulk bar never spends the debounce window describing a
                // selection the next result set will not contain.
                onChange={(next) => {
                  exitSelection();
                  setSearchInput(next);
                }}
                aria-label={t('projectDetail.assetsSection.searchAssets')}
                placeholder={t('projectDetail.assetsSection.searchAssets')}
                className="w-80"
              />

              <FilterChipPopover
                size="lg"
                label={t('projectDetail.assetsSection.status')}
                options={assetStatusChoices}
                value={statusTab === 'all' ? null : statusTab}
                onChange={(next) => updateStatusTab(next ?? 'all')}
                resetOptionLabel={t('projectDetail.assetsSection.allStatuses')}
                searchable={false}
              />

              <SortChipPopover
                size="lg"
                label={t('projectDetail.assetsSection.sort')}
                showTriggerLabel
                triggerLabelStyle="value"
                options={assetSortChoices}
                value={sortValue}
                onChange={updateSortValue}
                resetOptionLabel={t('projectDetail.assetsSection.newestFirst')}
                triggerAriaLabel={t('projectDetail.assetsSection.sortAssets')}
              />

              <div className="ml-auto flex items-center gap-xs">
                {isError && (
                  <button
                    type="button"
                    onClick={resetError}
                    className="text-sm text-destructive hover:underline"
                  >
                    {t('projectDetail.assetsSection.uploadFailedClickToDismiss')}
                  </button>
                )}
              </div>
            </motion.div>

            {/* Arrives over the toolbar rather than in place of it: an overlay
                dropping in, which is why it is the one block here that moves
                down rather than up. */}
            <AnimatePresence initial={false}>
              {selectionMode && (
                <motion.div
                  data-testid="bulk-actions-bar"
                  initial={shouldReduceMotion ? false : { opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
                  transition={{ duration: durations.base, ease: eases.quadInOut }}
                  className="absolute inset-x-0 -inset-y-1 z-10 flex items-center gap-xs rounded-full border border-border bg-surface px-md py-xs shadow-sm"
                >
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={exitSelection}
                    disabled={bulkApprove.isPending}
                    aria-label={t('projectDetail.assetsSection.cancel')}
                  >
                    <X className="size-4" />
                  </Button>

                  <span className="text-body-md font-medium text-foreground">
                    {isEveryFolderAssetSelected
                      ? t('projectDetail.assetsSection.allAssetsSelected', {
                          count: selectedCount,
                        })
                      : t('projectDetail.assetsSection.selectedCount', { selectedCount })}
                  </span>

                  {!isEveryLoadedAssetSelected && (
                    <Button
                      variant="ghost"
                      size="mini"
                      className="ml-xs font-medium text-primary"
                      onClick={selectAll}
                    >
                      {t('projectDetail.assetsSection.selectAll')}
                    </Button>
                  )}

                  <div className="ml-auto flex items-center gap-xs">
                    {/* Bulk approve and unapprove both go through the caller-only engine
                        paths, which refuse a manager who is not an assignee — every asset
                        comes back failed. The list DTO carries no assignee identities, so
                        the only signal available here is whether the caller is a manager at
                        all. Hidden rather than disabled: there is nothing they could select
                        to make it work. */}
                    {!isManager && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleBulkApprove}
                          disabled={!canApproveSelection || bulkApprove.isPending}
                          data-testid="bulk-action-approve"
                        >
                          <Check className="size-4" />
                          {t('projectDetail.assetsSection.approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIsUnapproveDialogOpen(true)}
                          disabled={!canUnapproveSelection}
                          data-testid="bulk-action-unapprove"
                        >
                          <X className="size-4" />
                          {t('projectDetail.assetsSection.markInReview')}
                        </Button>
                      </>
                    )}
                    <ScopedPermission permissions={[PROJECT_PERMISSIONS.ManageApprovalChains]}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsBulkDialogOpen(true)}
                        data-testid="bulk-action-assign-approval-chain"
                      >
                        <Workflow className="size-4" />
                        {t('projectDetail.assetsSection.assignApprovalChain')}
                      </Button>
                    </ScopedPermission>
                    {bulkRemoveApprovalChainEnabled && (
                      <ScopedPermission permissions={[PROJECT_PERMISSIONS.ManageApprovalChains]}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIsBulkRemoveDialogOpen(true)}
                          disabled={chainsInSelection.length === 0}
                          data-testid="bulk-action-remove-approval-chain"
                        >
                          <Trash2Icon className="size-4" />
                          {t('projectDetail.assetsSection.removeApprovalChain')}
                        </Button>
                      </ScopedPermission>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* A column, so the empty state's own `flex-1` has something to fill
              and `Empty` can centre itself in the space the grid would occupy. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {hasNoMatches ? (
              <EmptyState
                role="status"
                data-testid="assets-no-matches"
                className="flex-1"
                title={t('projectDetail.assetsSection.noMatchingAssets')}
                description={t('projectDetail.assetsSection.tryADifferentSearchOr')}
              >
                <Button variant="outline" onClick={clearFilters}>
                  {t('projectDetail.assetsSection.clearFilters')}
                </Button>
              </EmptyState>
            ) : (
              <AssetGrid
                projectId={projectId}
                folderId={folderId}
                files={files}
                isLoading={isFilesLoading}
                selectionMode={selectionMode}
                canSelect={approvalChainsEnabled && canEditDetails}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
              />
            )}
          </div>

          {approvalChainsEnabled && (
            <>
              <BulkAssignApprovalChainDialog
                key={isBulkDialogOpen ? 'bulk-assign-open' : 'bulk-assign-closed'}
                open={isBulkDialogOpen}
                projectId={projectId}
                folderId={folderId}
                assetIds={selectedAssetIds}
                onClose={() => setIsBulkDialogOpen(false)}
                onAssigned={exitSelection}
              />
              {bulkRemoveApprovalChainEnabled && (
                <BulkRemoveApprovalChainDialog
                  key={isBulkRemoveDialogOpen ? 'bulk-remove-open' : 'bulk-remove-closed'}
                  open={isBulkRemoveDialogOpen}
                  projectId={projectId}
                  folderId={folderId}
                  chainsInSelection={chainsInSelection}
                  selectedCount={selectedCount}
                  onClose={() => setIsBulkRemoveDialogOpen(false)}
                  onRemoved={exitSelection}
                />
              )}
              <BulkUnapproveDialog
                open={isUnapproveDialogOpen}
                projectId={projectId}
                folderId={folderId}
                assetIds={selectedAssetIds}
                onClose={() => setIsUnapproveDialogOpen(false)}
                onUnapproved={exitSelection}
              />
            </>
          )}
        </>
      )}
    </ProjectFileUploadGuard>
  );
}
